import React, { useState } from 'react';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { MessagesSquare, Settings, Import, FilePlus, Sparkles } from 'lucide-react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import PaginationButton from '@/components/ui/pagination-button';
import { Checkbox } from '@/components/ui/checkbox';
import { tableHeadClass, tableCellClass } from '@/components/ui/table-styles';
import SimpleHeader from '@/components/simple-header';
import {Route} from "@/routes/(inventory)/branch/_layout/automations/$id/index";
import dayjs from 'dayjs';
import { toast } from '@/components/ui/use-toast';
import { pharmacyClient } from '@/connect';
import { useRouter } from '@tanstack/react-router';

const Screen = () => {
  const [selected, setSelected] = useState<string[]>([]);

  const data = Route.useLoaderData();

  const allSelected = selected.length === data.automations.length;

  const handleSelectAll = () => {
    if (allSelected) {
      setSelected([]);
    } else {
      setSelected(data.automations.map((row) => row.id.toString()));
    }
  };

  const handleSelectRow = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  // Static params for Select
  const params = { stockPointId: 'all', page: 1, pageSize: 10 };

  // Reusable editable cell component
  type EditableCellProps<T> = {
    value: number;
    row: T;
    field: keyof T;
    onUpdate: (newValue: number, row: T, field: keyof T, updatingIsAutomation: boolean) => void;
  };

  function EditableCell<T extends { [key: string]: any }>({ value, row, field, onUpdate }: EditableCellProps<T>) {
    const [editing, setEditing] = React.useState(false);
    const [inputValue, setInputValue] = React.useState(value.toFixed(2));
    const inputRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
      if (editing && inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, [editing]);

    const handleBlur = () => {
      setEditing(false);
      const newValue = parseFloat(inputValue);
      if (!isNaN(newValue) && newValue !== value) {
        onUpdate(newValue, row, field, false);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        inputRef.current?.blur();
      } else if (e.key === 'Escape') {
        setInputValue(value.toFixed(2));
        setEditing(false);
      }
    };

    return (
      <div className="flex items-center">
        <div className="inline-block w-3 h-3 rounded-full mr-2 cursor-pointer" style={{ backgroundColor: row.isAutomated ? '#22c55e' : '#d1d5db' }} onClick={() => {
          // Change automation status logic here
          onUpdate(value, row, field, true);
        }}/>
        {editing ? (
          <input
            ref={inputRef}
            type="number"
            step="0.01"
            min="0"
            className="bg-transparent border-none outline-none w-16 px-0 text-right font-medium"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
          />
        ) : (
          <span
            className="cursor-pointer select-none w-16 text-right font-medium underline"
            onClick={() => setEditing(true)}
          >
            {value.toFixed(2)}
          </span>
        )}
      </div>
    );
  }
  const router = useRouter();

  return (

    <>
      <SimpleHeader text="Inventory Automation" />
      <div className="flex items-center justify-between">
        <div></div>
        <div className="flex items-center gap-4 pr-10">
          <span className="flex items-center gap-2 border bg-gray-100 px-2 py-1 rounded-md font-semibold text-muted-foreground text-xs">
            <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: '#22c55e' }} />
            Automated
          </span>
          <span className="flex items-center gap-2 border bg-gray-100 px-2 py-1 rounded-md font-semibold text-muted-foreground text-xs">
            <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: '#d1d5db' }} />
            Manual
          </span>
        </div>
        {/* Department Select (static) */}
        {/* <Select value={params.stockPointId} onValueChange={() => {}}>
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder="Select department...">
              {(() => {
                const selected = stockPoints.find(opt => opt.id === params.stockPointId) || { id: 'all', name: 'All Stock Points' };
                const idx = stockPoints.findIndex(opt => opt.id === selected.id);
                const color = idx > 0 ? departmentColors[(idx - 1) % departmentColors.length] : undefined;
                return selected ? (
                  <span className="flex items-center gap-2">
                    {selected.id !== 'all' && <span style={{ background: color, borderRadius: '4px', width: 16, height: 16, display: 'inline-block' }} />}
                    {selected.name}
                  </span>
                ) : null;
              })()}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              <span className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                All Stock Points
              </span>
            </SelectItem>
            {stockPoints.map((option, idx) => (
              <SelectItem key={option.id} value={option.id}>
                <span className="flex items-center gap-2">
                  {option.id !== 'all' && <span style={{ background: departmentColors[(idx - 1) % departmentColors.length], borderRadius: '4px', width: 16, height: 16, display: 'inline-block' }} />}
                  {option.name}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select> */}
        {/* Right side buttons (enabled) */}
        {/* <div className="flex gap-2 items-center">
          <Button size="sm" variant="outline" aria-label="Chat">
            <MessagesSquare className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="outline" aria-label="View Settings">
            <Settings className="w-4 h-4 mr-1" />
            View Settings
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger>
              <Button size="sm" variant="outline" aria-label="Import/Export">
                <Import className="w-4 h-4 mr-1" />
                Import/Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem>Import</DropdownMenuItem>
              <DropdownMenuItem>Export</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button size="sm" variant="outline" aria-label="Transfer">
            <FilePlus className="w-4 h-4 mr-1" />
            Transfer
          </Button>
        </div> */}
      </div>
      {/* Table Section */}
      <div className="mt-4 border rounded-lg bg-white overflow-hidden">
        <Table className="text-sm">
          <TableHeader>
            <TableRow className="text-sm">
              <TableHead className={tableHeadClass}>
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={handleSelectAll}
                  aria-label="Select all rows"
                />
              </TableHead>
              <TableHead className={tableHeadClass}>Trade Name</TableHead>
              <TableHead className={tableHeadClass}>Unit</TableHead>
              <TableHead className={tableHeadClass}>Unit Type</TableHead>
              <TableHead className={tableHeadClass}>Safety Stock</TableHead>
              <TableHead className={tableHeadClass}>Current Stock</TableHead>
              <TableHead className={tableHeadClass}>Minimum</TableHead>
              <TableHead className={tableHeadClass}>Maximum</TableHead>
              <TableHead className={tableHeadClass}>Last Interaction</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.automations.map((row) => (
              <TableRow className="font-medium" key={row.id}>
                <TableCell className={tableCellClass}>
                  <Checkbox
                    checked={selected.includes(row.id.toString())}
                    onCheckedChange={() => handleSelectRow(row.id.toString())}
                    aria-label={`Select row for ${row.tradeName}`}
                  />
                </TableCell>
                <TableCell className={tableCellClass}>{row.tradeName}</TableCell>
                <TableCell className={tableCellClass}>{row.unit}</TableCell>
                <TableCell className={tableCellClass}>{row.unitType}</TableCell>
                <TableCell className={tableCellClass}>
                  <EditableCell
                    value={row.safetyStock}
                    row={row}
                    field="safetyStock"
                    onUpdate={async (newValue, row, field, updatingIsAutomation) => {
                      try {
                        await pharmacyClient.updateMinMax({
                          safetyStock: newValue,
                          automationId: row.id,
                          minQuantity: row.minQuantity,
                          maxQuantity: row.maxQuantity,
                          isAutomated: updatingIsAutomation ? !row.isAutomated : row.isAutomated
                        })
                        router.invalidate();
                        toast({
                          title: 'Success',
                          description: 'Safety stock updated successfully',
                          variant: 'success',
                          duration: 3000,
                        });
                      } catch (error) {
                        toast({
                          title: 'Error',
                          description: 'Failed to update safety stock',
                          variant: 'destructive',
                          duration: 3000,
                        });
                      }
                    }}
                  />
                </TableCell>
                <TableCell className={tableCellClass}>{row.currentQuantity.toFixed(2)}</TableCell>
                <TableCell className={tableCellClass}>
                  <EditableCell
                    value={row.minQuantity}
                    row={row}
                    field="minQuantity"
                    onUpdate={async (newValue, row, field, updatingIsAutomation) => {
                      try {
                        await pharmacyClient.updateMinMax({
                          safetyStock: row.safetyStock,
                          automationId: row.id,
                          minQuantity: newValue,
                          maxQuantity: row.maxQuantity,
                          isAutomated: updatingIsAutomation ? !row.isAutomated : row.isAutomated
                        })
                        router.invalidate();
                        toast({
                          title: 'Success',
                          description: 'Minimum quantity updated successfully',
                          variant: 'success',
                          duration: 3000,
                        });
                      } catch (error) {
                        toast({
                          title: 'Error',
                          description: 'Failed to update minimum quantity',
                          variant: 'destructive',
                          duration: 3000,
                        });
                      }
                    }}
                  />
                </TableCell>
                <TableCell className={tableCellClass}>
                  <EditableCell
                    value={row.maxQuantity}
                    row={row}
                    field="maxQuantity"
                    onUpdate={async (newValue, row, field, updatingIsAutomation) => {
                      try {
                        await pharmacyClient.updateMinMax({
                          safetyStock: row.safetyStock,
                          automationId: row.id,
                          minQuantity: row.minQuantity,
                          maxQuantity: newValue,
                          isAutomated: updatingIsAutomation ? !row.isAutomated : row.isAutomated
                        })
                        router.invalidate();
                        toast({
                          title: 'Success',
                          description: 'Maximum quantity updated successfully',
                          variant: 'success',
                          duration: 3000,
                        });
                      } catch (error) {
                        toast({
                          title: 'Error',
                          description: 'Failed to update maximum quantity',
                          variant: 'destructive',
                          duration: 3000,
                        });
                      }
                    }}
                  />
                </TableCell>
                <TableCell className={tableCellClass}>{dayjs(row.lastUpdated?.toDate()).format("DD/MM/YYYY HH:mm:ss")}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="flex items-center justify-between py-4 px-6 text-sm border-t bg-white">
          <p>{data.totalCount} <span className="text-gray-500 font-light">count</span> | <span className="text-gray-500 font-light">Last Updated:</span> {
            dayjs(
              data.automations.length > 0
                ? (
                    data.automations
                      .slice() // avoid mutating original array
                      .sort((a, b) => {
                        const aTime = a.lastUpdated?.toDate?.()?.getTime?.() ?? 0;
                        const bTime = b.lastUpdated?.toDate?.()?.getTime?.() ?? 0;
                        return bTime - aTime;
                      })[0].lastUpdated?.toDate?.() ?? new Date()
                  )
                : new Date()
            ).format("DD/MM/YYYY HH:mm:ss")
          }</p>
          <div className="flex items-center gap-1">
            <PaginationButton disabled onClick={() => {}}> &laquo; </PaginationButton>
            <PaginationButton disabled onClick={() => {}}> &lsaquo; </PaginationButton>
            <PaginationButton active onClick={() => {}}>1</PaginationButton>
            <PaginationButton disabled onClick={() => {}}> &rsaquo; </PaginationButton>
            <PaginationButton disabled onClick={() => {}}> &raquo; </PaginationButton>
          </div>
        </div>
      </div>
    </>
  );
};

export default Screen;